import type * as Fields from "../types/api-response-fields";

import * as React from "react";

import { getLangFromLocalStorage, getSiteID } from "../utils";
import { usePage } from "./usePage";
import { useSite } from "./useSite";
import { getOptions } from "./utils";

/**
 * `useContentTypeNavigation<ContentType>()`
 *
 * Returns an object with previous and next ContentType items.
 * This hooks only works in the context of the Griddo editor.
 * @param data The property of the ReferenceField.
 *
 * @example
 * const { previous, next } = useContentTypeNavigation<NewsContentType>(data)
 */
function useContentTypeNavigation<ContentType = unknown>(
	data?: Fields.ReferenceNavigation,
): Fields.QueriedNavigationDataItem<ContentType> {
	const [response, setResponse] = React.useState<
		Fields.QueriedNavigationDataItem<ContentType>
	>({ previous: [], next: [], error: undefined });

	const {
		apiUrl,
		canonicalURL,
		structuredDataContent,
		languageId: languageFromPageContext,
	} = usePage();
	const { renderer, siteId: siteIdFromProvider } = useSite();

	const {
		order = "recent-asc",
		quantity = 1,
		fullRelations = false,
		referenceId = structuredDataContent?.id,
	} = data || {};

	React.useEffect(() => {
		const languageFromLocalStorage = getLangFromLocalStorage();
		const isEditorScope = !!languageFromLocalStorage;
		const languageId = languageFromLocalStorage || languageFromPageContext;
		const isSharedPageScope = renderer === "sharedPage";

		if (!isEditorScope && !isSharedPageScope) return;

		const siteID =
			getSiteID() || canonicalURL || siteIdFromProvider || "global";
		const url = `${apiUrl}/site/${siteID}/distributor`;

		const body = {
			mode: "navigation",
			order,
			quantity,
			fullRelations,
			referenceId,
		};

		const fetchOptions = getOptions(body, {
			lang: languageId,
			authMode: isEditorScope ? "JWT" : "PREVIEW",
		});

		isEditorScope &&
			fetch(url, fetchOptions)
				.then((res) => res.json())
				.then((data) => setResponse(data))
				.catch((error) => {
					console.error("Error:", error);
				});
	}, [
		apiUrl,
		canonicalURL,
		fullRelations,
		order,
		quantity,
		referenceId,
		renderer,
		siteIdFromProvider,
	]);

	return response;
}

export { useContentTypeNavigation };
